import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
  lazyRouteComponent,
  ErrorComponent,
} from '@tanstack/react-router';
import { RootBoot } from './root-boot';
import { PublicLayout } from './public-layout';
import { AppShell } from './app-shell';
import { AdminShell } from './admin-shell';
import { AuthLayout } from './auth-layout';
import { NotFound } from '@/routes/not-found';
import { HomePage } from '@/routes/home';
import { LoginPage } from '@/routes/login';
import { SignupPage } from '@/routes/signup';
import { VerifyPage } from '@/routes/verify';
import { ForgotPage } from '@/routes/forgot';
import { TermsPage } from '@/routes/legal/terms';
import { PrivacyPage } from '@/routes/legal/privacy';
import { CommunityPage } from '@/routes/legal/community';
import { SafetyPage } from '@/routes/legal/safety';
import { OnboardingTermsPage } from '@/routes/onboarding/terms';
import { OnboardingProfilePage } from '@/routes/onboarding/profile';
import { OnboardingHowToPage } from '@/routes/onboarding/how-to';
import { OnboardingLocationPage } from '@/routes/onboarding/location';
import { sessionSnapshot } from '@/lib/session';

const lazy = (loader: () => Promise<{ default: React.ComponentType }>) =>
  lazyRouteComponent(loader);

const rootRoute = createRootRoute({ component: RootBoot, notFoundComponent: NotFound });

/* ---------- public ---------- */
const publicLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: 'public',
  component: PublicLayout,
});
const homeRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: '/',
  component: HomePage,
});
const termsRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: '/terms',
  component: TermsPage,
});
const privacyRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: '/privacy',
  component: PrivacyPage,
});
const communityRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: '/community-guidelines',
  component: CommunityPage,
});
const safetyRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: '/safety',
  component: SafetyPage,
});

/* ---------- auth ---------- */
const authLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: 'auth',
  component: AuthLayout,
  beforeLoad: () => {
    const s = sessionSnapshot();
    if (s.isAuthed && s.isOnboarded) throw redirect({ to: '/app' });
    if (s.isAuthed) throw redirect({ to: '/onboarding/terms' });
  },
});
const loginRoute = createRoute({
  getParentRoute: () => authLayout,
  path: '/login',
  component: LoginPage,
});
const signupRoute = createRoute({
  getParentRoute: () => authLayout,
  path: '/signup',
  component: SignupPage,
});
const verifyRoute = createRoute({
  getParentRoute: () => authLayout,
  path: '/verify',
  component: VerifyPage,
});
const forgotRoute = createRoute({
  getParentRoute: () => authLayout,
  path: '/forgot',
  component: ForgotPage,
});

/* ---------- onboarding ---------- */
const onboardingLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: 'onboarding',
  component: AuthLayout,
  beforeLoad: () => {
    const s = sessionSnapshot();
    if (!s.isAuthed) throw redirect({ to: '/login' });
    if (s.isOnboarded) throw redirect({ to: '/app' });
  },
});
const obTerms = createRoute({
  getParentRoute: () => onboardingLayout,
  path: '/onboarding/terms',
  component: OnboardingTermsPage,
});
const obProfile = createRoute({
  getParentRoute: () => onboardingLayout,
  path: '/onboarding/profile',
  component: OnboardingProfilePage,
});
const obHowTo = createRoute({
  getParentRoute: () => onboardingLayout,
  path: '/onboarding/how-to',
  component: OnboardingHowToPage,
});
const obLocation = createRoute({
  getParentRoute: () => onboardingLayout,
  path: '/onboarding/location',
  component: OnboardingLocationPage,
});

/* ---------- app (auth + onboarded) ---------- */
const appLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: AppShell,
  beforeLoad: () => {
    const s = sessionSnapshot();
    if (!s.isAuthed) throw redirect({ to: '/login' });
    if (!s.isOnboarded) throw redirect({ to: '/onboarding/terms' });
  },
});
const appRoutes = [
  createRoute({
    getParentRoute: () => appLayout,
    path: '/app',
    component: lazy(() => import('@/routes/app/radar')),
  }),
  createRoute({
    getParentRoute: () => appLayout,
    path: '/app/spots',
    component: lazy(() => import('@/routes/app/spots')),
  }),
  createRoute({
    getParentRoute: () => appLayout,
    path: '/app/spots/$spotId',
    component: lazy(() => import('@/routes/app/spot')),
  }),
  createRoute({
    getParentRoute: () => appLayout,
    path: '/app/whispers',
    component: lazy(() => import('@/routes/app/whispers')),
  }),
  createRoute({
    getParentRoute: () => appLayout,
    path: '/app/whispers/$threadId',
    component: lazy(() => import('@/routes/app/whisper')),
  }),
  createRoute({
    getParentRoute: () => appLayout,
    path: '/app/circles',
    component: lazy(() => import('@/routes/app/circles')),
  }),
  createRoute({
    getParentRoute: () => appLayout,
    path: '/app/pro',
    component: lazy(() => import('@/routes/app/pro')),
  }),
  createRoute({
    getParentRoute: () => appLayout,
    path: '/app/pro/$conversationId',
    component: lazy(() => import('@/routes/app/pro-thread')),
  }),
  createRoute({
    getParentRoute: () => appLayout,
    path: '/app/circles/$roomId',
    component: lazy(() => import('@/routes/app/circle')),
  }),
  createRoute({
    getParentRoute: () => appLayout,
    path: '/app/notifications',
    component: lazy(() => import('@/routes/app/notifications')),
  }),
  createRoute({
    getParentRoute: () => appLayout,
    path: '/app/me',
    component: lazy(() => import('@/routes/app/me')),
  }),
  createRoute({
    getParentRoute: () => appLayout,
    path: '/app/appeal',
    component: lazy(() => import('@/routes/app/appeal')),
  }),
];

/* ---------- admin ---------- */
const adminLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: 'admin',
  component: AdminShell,
  beforeLoad: () => {
    const s = sessionSnapshot();
    if (!s.isAuthed) throw redirect({ to: '/login' });
    if (!s.isAdmin) throw redirect({ to: '/app' });
  },
});
const adminRoutes = [
  createRoute({
    getParentRoute: () => adminLayout,
    path: '/admin',
    component: lazy(() => import('@/routes/admin/dashboard')),
  }),
  createRoute({
    getParentRoute: () => adminLayout,
    path: '/admin/reports',
    component: lazy(() => import('@/routes/admin/reports')),
  }),
  createRoute({
    getParentRoute: () => adminLayout,
    path: '/admin/users',
    component: lazy(() => import('@/routes/admin/users')),
  }),
  createRoute({
    getParentRoute: () => adminLayout,
    path: '/admin/rooms',
    component: lazy(() => import('@/routes/admin/rooms')),
  }),
  createRoute({
    getParentRoute: () => adminLayout,
    path: '/admin/pro',
    component: lazy(() => import('@/routes/admin/pro')),
  }),
  createRoute({
    getParentRoute: () => adminLayout,
    path: '/admin/media',
    component: lazy(() => import('@/routes/admin/media')),
  }),
  createRoute({
    getParentRoute: () => adminLayout,
    path: '/admin/spots',
    component: lazy(() => import('@/routes/admin/spots')),
  }),
  createRoute({
    getParentRoute: () => adminLayout,
    path: '/admin/audit',
    component: lazy(() => import('@/routes/admin/audit')),
  }),
  createRoute({
    getParentRoute: () => adminLayout,
    path: '/admin/config',
    component: lazy(() => import('@/routes/admin/config')),
  }),
  createRoute({
    getParentRoute: () => adminLayout,
    path: '/admin/moderators',
    component: lazy(() => import('@/routes/admin/moderators')),
  }),
  createRoute({
    getParentRoute: () => adminLayout,
    path: '/admin/metrics',
    component: lazy(() => import('@/routes/admin/metrics')),
  }),
  createRoute({
    getParentRoute: () => adminLayout,
    path: '/admin/broadcast',
    component: lazy(() => import('@/routes/admin/broadcast')),
  }),
  createRoute({
    getParentRoute: () => adminLayout,
    path: '/admin/appeals',
    component: lazy(() => import('@/routes/admin/appeals')),
  }),
];

const routeTree = rootRoute.addChildren([
  publicLayout.addChildren([homeRoute, termsRoute, privacyRoute, communityRoute, safetyRoute]),
  authLayout.addChildren([loginRoute, signupRoute, verifyRoute, forgotRoute]),
  onboardingLayout.addChildren([obTerms, obProfile, obHowTo, obLocation]),
  appLayout.addChildren(appRoutes),
  adminLayout.addChildren(adminRoutes),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultErrorComponent: ({ error }) => <ErrorComponent error={error} />,
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
